// Batch face-photo upload (SPEC-019 / ADR-028). The admin picks many image files
// named by employee_code (e.g. A1234.jpg); each is read to base64 and POSTed as
// one batch. One PDPA consent attestation covers the batch. The per-row result
// mirrors the CSV import (created / rejected / errors). Modeled on ImportDialog.

import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useI18n } from "@faceheart/i18n";
import type { ImportResult, ImportRowError } from "@faceheart/api-types";
import { employeesApi } from "../../lib/api/endpoints";
import { ApiError } from "../../lib/api/client";
import { ProgressBar } from "../ui/ProgressBar";

const CODE_RE = /^([0-9]{7}|[A-Za-z][0-9]{4})$/;
const FACE_CONSENT_VERSION = "v1.0";

// Upload size budget. The prod backend is a buffered Lambda behind API Gateway
// (~6 MB request hard cap), so we chunk into sub-batches well under that and
// reject any single photo that alone can't fit. base64 inflates bytes ~1.37×.
const BASE64_INFLATION = 1.37;
const MAX_BATCH_RAW_BYTES = 4 * 1024 * 1024; // ~4 MB raw → ~5.5 MB base64 per POST
const MAX_PHOTO_RAW_BYTES = 4 * 1024 * 1024; // a single photo must fit one batch
const MAX_PHOTO_MB = Math.round(MAX_PHOTO_RAW_BYTES / (1024 * 1024));

type Picked = { file: File; code: string; valid: boolean; tooBig: boolean };

// Derive the employee_code from a filename: drop directory + extension, then
// strip a trailing disambiguator (_1, -2, (3)) so several photos of one person
// — A1234_1.jpg, A1234-2.jpg, A1234(3).jpg — all map to A1234. Only digits
// preceded by a separator are stripped, so a bare 7-digit code (1234567) is
// left intact while 1234567_1 → 1234567.
function codeFromFilename(name: string): string {
  const base = name.replace(/^.*[\\/]/, "").replace(/\.[^.]+$/, "");
  return base.replace(/[\s._-]*(?:\(\d+\)|[_-]\d+)$/, "").trim();
}

// Split valid photos into sub-batches each under the per-request byte budget so
// no single POST exceeds the Lambda/API-Gateway limit.
function chunkBySize(items: Picked[]): Picked[][] {
  const batches: Picked[][] = [];
  let cur: Picked[] = [];
  let curBytes = 0;
  for (const p of items) {
    const est = p.file.size * BASE64_INFLATION;
    if (cur.length > 0 && curBytes + est > MAX_BATCH_RAW_BYTES * BASE64_INFLATION) {
      batches.push(cur);
      cur = [];
      curBytes = 0;
    }
    cur.push(p);
    curBytes += est;
  }
  if (cur.length > 0) batches.push(cur);
  return batches;
}

function readBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result ?? "").split(",")[1] ?? "");
    reader.onerror = () => reject(new Error("read failed"));
    reader.readAsDataURL(file);
  });
}

export function FaceBatchDialog({ onClose }: { onClose: () => void }) {
  const { t } = useI18n("admin");
  const qc = useQueryClient();
  const [picked, setPicked] = useState<Picked[]>([]);
  const [consent, setConsent] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Two-phase progress: reading files (base64) then uploading sub-batches.
  const [phase, setPhase] = useState<"idle" | "reading" | "uploading">("idle");
  const [readDone, setReadDone] = useState(0);
  const [readTotal, setReadTotal] = useState(0);
  const [batchDone, setBatchDone] = useState(0);
  const [batchTotal, setBatchTotal] = useState(0);

  const mut = useMutation({
    mutationFn: async () => {
      const valid = picked.filter((p) => p.valid && !p.tooBig);
      const batches = chunkBySize(valid);
      setBatchTotal(batches.length);
      setBatchDone(0);
      setReadTotal(valid.length);
      setReadDone(0);

      // Merge each sub-batch's ImportResult into one cumulative result. Rows are
      // renumbered across batches so the per-row "row" stays globally unique.
      const merged: ImportResult = { created: 0, updated: 0, rejected: 0, errors: [] };
      let rowBase = 0;
      for (const batch of batches) {
        setPhase("reading");
        const items = [];
        for (const p of batch) {
          items.push({
            employee_code: p.code,
            frame: await readBase64(p.file),
            consent_version: FACE_CONSENT_VERSION,
          });
          setReadDone((n) => n + 1);
        }
        setPhase("uploading");
        const res = await employeesApi.enrollFaceBatch(items);
        merged.created += res.created;
        merged.updated += res.updated;
        merged.rejected += res.rejected;
        for (const e of res.errors ?? []) {
          merged.errors.push({ ...e, row: e.row + rowBase } as ImportRowError);
        }
        rowBase += batch.length;
        setBatchDone((n) => n + 1);
      }
      return merged;
    },
    onSuccess: (res: ImportResult) => {
      if (res.created > 0 || res.updated > 0)
        void qc.invalidateQueries({ queryKey: ["employees"] });
    },
    onSettled: () => setPhase("idle"),
  });

  function onPick(e: React.ChangeEvent<HTMLInputElement>) {
    const files = Array.from(e.target.files ?? []);
    setError(null);
    setPicked(
      files.map((file) => {
        const code = codeFromFilename(file.name);
        const tooBig = file.size > MAX_PHOTO_RAW_BYTES;
        return { file, code, valid: CODE_RE.test(code) && !tooBig, tooBig };
      }),
    );
  }

  function submit() {
    setError(null);
    const valid = picked.filter((p) => p.valid && !p.tooBig);
    if (valid.length === 0) {
      setError(t("employees.faceBatch.noValid"));
      return;
    }
    if (picked.some((p) => p.tooBig)) {
      setError(t("employees.faceBatch.tooLarge", { mb: MAX_PHOTO_MB }));
    }
    mut.mutate();
  }

  const result = mut.data;
  const apiErr =
    mut.error instanceof ApiError
      ? t("employees.faceBatch.failed", { code: (mut.error as ApiError).code })
      : mut.error
        ? t("common:errors.network")
        : null;
  const validCount = picked.filter((p) => p.valid && !p.tooBig).length;

  return (
    <Backdrop onClose={onClose}>
      <div
        role="dialog"
        aria-modal="true"
        aria-label={t("employees.faceBatch.title")}
        className="w-140 max-w-[92vw] max-h-[88vh] overflow-y-auto rounded-lg border border-admin-border bg-admin-bg2 p-6 shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mb-4 flex items-start justify-between">
          <div>
            <h2 className="text-lg font-medium">
              {t("employees.faceBatch.title")}
            </h2>
            <p className="mt-1 text-xs text-admin-text3">
              {t("employees.faceBatch.subtitle")}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-admin-text3 hover:text-admin-text1"
            aria-label={t("common:actions.close")}
          >
            ✕
          </button>
        </div>

        <label className="mb-3 flex items-start gap-2 text-xs text-admin-text2">
          <input
            type="checkbox"
            checked={consent}
            onChange={(e) => setConsent(e.target.checked)}
            className="mt-0.5"
          />
          <span>{t("employees.faceBatch.consentLabel")}</span>
        </label>

        <input
          type="file"
          accept="image/*"
          multiple
          disabled={!consent}
          onChange={onPick}
          className="mb-3 block w-full text-sm text-admin-text2 disabled:opacity-50 file:mr-3 file:rounded file:border file:border-admin-border file:bg-admin-bg3 file:px-3 file:py-1.5 file:text-sm file:text-admin-text2"
        />

        {picked.length > 0 && (
          <div className="mb-3 max-h-40 overflow-y-auto rounded border border-admin-border bg-admin-bg3/60 px-3 py-2 text-xs">
            <div className="mb-1 text-admin-text3">
              {t("employees.faceBatch.picked", {
                valid: validCount,
                total: picked.length,
              })}
            </div>
            <ul className="space-y-0.5">
              {picked.map((p, i) => (
                <li key={i} className="flex gap-2">
                  <span
                    className={
                      p.valid ? "text-admin-text2" : "text-admin-red"
                    }
                  >
                    {p.file.name} → {p.code || "—"}
                    {p.tooBig
                      ? ` (>${MAX_PHOTO_MB}MB)`
                      : ""}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {(error || apiErr) && (
          <p className="mt-2 text-sm text-admin-red">{error ?? apiErr}</p>
        )}
        {result && <ResultPanel result={result} />}

        {mut.isPending && (
          <div className="mt-4">
            <ProgressBar
              value={
                phase === "reading"
                  ? readTotal > 0
                    ? readDone / readTotal
                    : 0
                  : batchTotal > 0
                    ? batchDone / batchTotal
                    : undefined
              }
              label={
                phase === "reading"
                  ? t("employees.faceBatch.reading")
                  : t("employees.faceBatch.uploading")
              }
            />
          </div>
        )}

        <div className="mt-5 flex justify-end gap-3">
          <button
            type="button"
            onClick={onClose}
            className="rounded border border-admin-border px-4 py-2 text-sm text-admin-text2 hover:bg-admin-bg3"
          >
            {result ? t("common:actions.close") : t("common:actions.cancel")}
          </button>
          <button
            type="button"
            onClick={submit}
            disabled={mut.isPending || !consent || validCount === 0}
            className="rounded bg-admin-cyan/90 px-4 py-2 text-sm font-medium text-admin-bg hover:bg-admin-cyan disabled:opacity-50"
          >
            {mut.isPending
              ? t("employees.faceBatch.uploading")
              : t("employees.faceBatch.submit")}
          </button>
        </div>
      </div>
    </Backdrop>
  );
}

function ResultPanel({ result }: { result: ImportResult }) {
  const { t } = useI18n("admin");
  return (
    <div className="mt-4 rounded border border-admin-border bg-admin-bg3/40 p-3">
      <div className="flex gap-6 text-sm">
        <span className="text-admin-green">
          {t("employees.faceBatch.created", { n: result.created })}
        </span>
        <span
          className={
            result.rejected > 0 ? "text-admin-red" : "text-admin-text3"
          }
        >
          {t("employees.faceBatch.rejected", { n: result.rejected })}
        </span>
      </div>
      {result.errors.length > 0 && (
        <ul className="mt-2 max-h-40 space-y-1 overflow-y-auto text-xs text-admin-text2">
          {result.errors.map((e, i) => (
            <li key={i} className="flex gap-2">
              <span className="shrink-0 text-admin-text3">
                {e.employee_code ? `${e.employee_code}` : `#${e.row}`}
              </span>
              <span className="text-admin-red">
                {e.reason
                  ? t(`employees.faceBatch.reason.${e.reason}`, {
                      defaultValue: e.message,
                    })
                  : e.message}
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function Backdrop({
  children,
  onClose,
}: {
  children: React.ReactNode;
  onClose: () => void;
}) {
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      onClick={onClose}
    >
      {children}
    </div>
  );
}
